import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from '../components/GlassCard';

const AboutPage = () => {
  const { theme } = useTheme();

  return (
    <div style={{ padding: '32px 0', maxWidth: '900px', margin: '0 auto' }}>
      <Link
        to="/"
        style={{
          color: theme.text.secondary,
          textDecoration: 'none',
          marginBottom: '24px',
          display: 'inline-block',
          fontSize: '14px',
        }}
      >
        ← Back to Home
      </Link>

      <GlassCard>
        <h1 style={{ fontSize: '36px', marginBottom: '16px', color: theme.text.primary }}>
          About Family Tree
        </h1>

        <div style={{ color: theme.text.secondary, lineHeight: '1.8', fontSize: '16px' }}>
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              What is Family Tree?
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree is a modern web application designed to help you build, manage, and visualize your family history. 
              Create detailed family trees, add information about family members, and collaborate with relatives to preserve 
              your family's legacy.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Key Features
            </h2>
            
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                👨‍👩‍👧‍👦 Family Management
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Create and organize multiple family groups. Each family can have its own members, relationships, 
                and shared information. Perfect for extended families or different branches of your family tree.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                👤 People Profiles
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Add detailed information about each family member including:
              </p>
              <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
                <li>Full name, preferred name, and nicknames</li>
                <li>Date of birth and date of death</li>
                <li>Location (city, country)</li>
                <li>Profile pictures</li>
                <li>Hobbies and interests</li>
                <li>Contact information (email, phone, WhatsApp)</li>
                <li>Social media links</li>
                <li>Personal notes and descriptions</li>
              </ul>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                🌳 Family Relationships
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Define and visualize family relationships:
              </p>
              <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
                <li>Parent-child relationships (biological, adoptive, step, guardian)</li>
                <li>Partnerships (spouse, partner)</li>
                <li>Family memberships with roles (viewer, editor, admin)</li>
              </ul>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                🔐 Secure Collaboration
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Invite family members to collaborate on your family tree. Control who can view and edit information 
                with role-based permissions. All data is securely stored and protected.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                🌳 Interactive Tree View
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Visualize your family tree with our interactive D3.js-based tree visualization. The tree automatically 
                adapts spacing based on the number of nodes and relationships. Features include:
              </p>
              <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
                <li>Automatic zoom and fit to viewport</li>
                <li>Dynamic tree layout optimization</li>
                <li>Click on any member to view detailed information</li>
                <li>Spouse information displayed alongside each member</li>
                <li>Fast tree building with optimized database queries</li>
              </ul>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: theme.text.primary }}>
                🎨 Beautiful Interface
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Enjoy a modern, glassmorphism design with dark and light mode support. The intuitive interface makes 
                it easy to navigate and manage your family information.
              </p>
            </div>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Getting Started
            </h2>
            <ol style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Sign In:</strong> Sign in to Family Tree using your email address and password. For security, 
                make sure to use a strong, unique password. (Sign-up is currently available by invitation only.)
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Create a Family:</strong> Start by creating your first family group. Give it a meaningful name 
                like "Smith Family" or "Maternal Side".
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Add People:</strong> Begin adding family members to your tree. Start with yourself and your 
                immediate family, then expand outward. Each member can have detailed profiles with photos, contact information, 
                and personal notes.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Define Relationships:</strong> Connect family members by establishing parent-child relationships 
                (biological, adoptive, step, or guardian) and partnerships (spouse or partner). The system will automatically 
                calculate extended relationships like siblings and in-laws.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Visualize Your Tree:</strong> Use the Tree View to see your family relationships in an interactive 
                visual format. The tree automatically adjusts to show all relationships clearly, with automatic zoom and 
                pan features for easy navigation.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Invite Family:</strong> Share your family tree with relatives by inviting them to join. They can 
                contribute their own information and help build the complete picture. You control who can view and edit 
                information through role-based permissions.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Explore & Search:</strong> Use the Members page to search through all family members and quickly 
                find information. Click on any member in the tree view to see their detailed profile and relationships.
              </li>
            </ol>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Privacy & Data Control
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You have complete control over your family data:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>You decide what information to share</li>
              <li>You control who can view and edit your family tree</li>
              <li>You can edit or remove information at any time</li>
              <li>You can request account deletion whenever you want</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              For more details, please review our{' '}
              <Link
                to="/privacy"
                style={{ color: theme.accent.primary, textDecoration: 'none' }}
              >
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link
                to="/terms"
                style={{ color: theme.accent.primary, textDecoration: 'none' }}
              >
                Terms of Service
              </Link>
              .
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Tips for Best Results
            </h2>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Start Small:</strong> Begin with your immediate family and gradually expand. Don't try to add 
                everyone at once.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Be Accurate:</strong> Verify information with family members before adding it. Accuracy is more 
                important than completeness.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Add Photos:</strong> Profile pictures help bring your family tree to life and make it easier 
                to identify family members.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Use Notes:</strong> The notes field is perfect for storing interesting stories, anecdotes, or 
                additional context about family members.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Collaborate:</strong> Invite family members to contribute. Multiple people working together can 
                build a more complete and accurate family tree.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Regular Updates:</strong> Keep your family tree current by adding new family members, updating 
                information, and maintaining relationships.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Need Help?
            </h2>
            <p style={{ marginBottom: '16px' }}>
              If you have questions or need assistance, please contact us:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px', listStyle: 'none' }}>
              <li style={{ marginBottom: '8px' }}>
                📧 Email:{' '}
                <a
                  href="mailto:twodotsai@gmail.com"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  twodotsai@gmail.com
                </a>
              </li>
              <li>
                🌐 Website:{' '}
                <a
                  href="https://ourfamilytree.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  https://ourfamilytree.cloud
                </a>
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              Performance & Technology
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree is built with performance in mind. Our tree visualization uses optimized database queries 
              and materialized views to ensure fast loading times, even for large family trees with hundreds of members. 
              The tree layout automatically adapts to your data, ensuring clear visualization regardless of family size.
            </p>
            <p style={{ marginBottom: '16px' }}>
              The application uses modern web technologies including React, D3.js for data visualization, and PostgreSQL 
              for robust data storage. All data is automatically synchronized and optimized for fast access.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              About Our Family Tree
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Our Family Tree is developed by Surya (Chanti) Manepalli, dedicated to creating useful and beautiful applications 
              that help people organize and preserve their important family information.
            </p>
            <p style={{ marginBottom: '16px' }}>
              We're committed to protecting your privacy and providing you with full control over your family data. 
              Your information is yours, and we're here to help you manage it effectively.
            </p>
            <p style={{ marginBottom: '16px' }}>
              We continuously improve Our Family Tree based on user feedback and needs. Your suggestions and feature requests 
              help us make the platform better for everyone.
            </p>
          </section>
        </div>
      </GlassCard>
    </div>
  );
};

export default AboutPage;






